import { Fragment, useRef, useState, type ReactNode } from "react";
import { HoverTip } from "@/components/ui/HoverTip";
import { wrappedIndex } from "@/lib/wrapped-index";
import type { BlockControl, TextBlock } from "@/lib/structured-text";
import { Button } from "./button";

/** Down the list or along the bar: one step either way, for whatever walks by arrow. */
export const STEPS: Partial<Record<string, number>> = {
  ArrowDown: 1,
  ArrowUp: -1,
};

/** A horizontal bar answers the arrows that run along it, which is what its role promises. */
const ALONG_THE_BAR: Partial<Record<string, number>> = {
  ArrowRight: 1,
  ArrowLeft: -1,
  ...STEPS,
};

/** One act on the bar: its tip, its state for the block the caret is in, and its press. */
function Act({
  control,
  block,
  focused,
  onFocus,
  onRun,
}: {
  control: BlockControl;
  block: TextBlock | null;
  focused: boolean;
  onFocus: () => void;
  onRun: (control: BlockControl) => void;
}) {
  const on = block !== null && (control.active?.(block) ?? false);
  const named =
    control.shortcut === undefined
      ? control.label
      : `${control.label} \u00b7 ${control.shortcut}`;
  return (
    <HoverTip wide={false} tone="dark" tip={named}>
      {({ triggerProps }) => (
        <Button
          {...triggerProps}
          type="button"
          aria-label={control.label}
          variant={on ? "primary" : "quiet"}
          size="dense"
          tabIndex={focused ? 0 : -1}
          aria-pressed={control.active === undefined ? undefined : on}
          disabled={block === null || !(control.enabled?.(block) ?? true)}
          // A press keeps the caret in the words, so the block being set is
          // still the block the writer was in.
          onMouseDown={(event) => {
            event.preventDefault();
          }}
          onFocus={() => {
            triggerProps.onFocus();
            onFocus();
          }}
          onClick={() => {
            onRun(control);
          }}
        >
          {control.icon}
          {control.label}
        </Button>
      )}
    </HoverTip>
  );
}

/**
 * The editor's own bar, over the words it acts on.
 *
 * Part of the editor rather than a row of buttons above it, so a press on it
 * plainly acts on the field under it. A bar that comes and goes is drawn over
 * what is above the words rather than in a row of its own, so the claim does
 * not move as the attorney clicks into it; a bar that stays shares one bordered
 * surface with the words, divided by a rule. Quiet icons either way, because a
 * bar that shouts competes with the words it is there to shape.
 *
 * One tab stop for the whole bar, walked with the arrows, which is what a
 * toolbar is: a writer moving on from the words should reach what follows them,
 * not each control in turn. Each act also answers to the keys it shows, since
 * nothing moves focus into a bar that is only there while the words have it.
 */
export function Controls({
  controls,
  block,
  note,
  onRun,
}: {
  controls: readonly BlockControl[];
  block: TextBlock | null;
  note?: ReactNode;
  onRun: (control: BlockControl) => void;
}) {
  const [at, setAt] = useState(0);
  const barRef = useRef<HTMLDivElement>(null);
  const step = (by: number) => {
    const buttons = [...(barRef.current?.querySelectorAll("button") ?? [])];
    // Over one that cannot be pressed: a disabled control ignores focus, and
    // the bar's one tab stop would sit on a button no key could reach.
    let next = at;
    for (let tried = 0; tried < buttons.length; tried += 1) {
      next = wrappedIndex(next, by, buttons.length);
      if (!buttons[next].disabled) break;
    }
    setAt(next);
    buttons[next]?.focus();
  };
  return (
    <div
      ref={barRef}
      role="toolbar"
      aria-label="Ways to set this line"
      aria-orientation="horizontal"
      // Over the words rather than above them: a bar that took a row of its own
      // would push the claim down as the attorney clicked into it, moving the
      // words out from under the pointer that chose them. Over the middle of what
      // is above the words, which is the part of a row the design leaves empty:
      // its label sits at one end and its acts at the other, and a bar at either
      // end would cover one of them.
      className="absolute bottom-full left-1/2 z-20 mb-1.5 flex max-w-full -translate-x-1/2 items-center gap-1 rounded-[10px] border border-border bg-card px-1.5 py-1 shadow-popover"
      onKeyDown={(event) => {
        const by = ALONG_THE_BAR[event.key];
        if (by === undefined) return;
        event.preventDefault();
        step(by);
      }}
    >
      {controls.map((control, index) => (
        <Fragment key={control.id}>
          {index > 0 && control.group !== controls[index - 1].group && (
            <span aria-hidden className="mx-0.5 h-4 w-px flex-none bg-border" />
          )}
          <Act
            control={control}
            block={block}
            focused={index === at}
            onFocus={() => {
              setAt(index);
            }}
            onRun={onRun}
          />
        </Fragment>
      ))}
      {note !== undefined && (
        // Gone in a narrow column, where the bar would be wider than the room
        // above the words and would cover what is written there. What it says is
        // on each act's own tip, which a narrow column still has.
        <>
          <span
            aria-hidden
            className="mx-0.5 hidden h-4 w-px flex-none bg-border xl:block"
          />
          <span className="hidden min-w-0 truncate pr-1 pl-0.5 text-[10px] font-medium text-subtle-foreground xl:inline">
            {note}
          </span>
        </>
      )}
    </div>
  );
}
